import React, { useState } from 'react';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, TrendingDown, Info, RefreshCw } from 'lucide-react';
import { useGetMyBalanceHistory, useGetMyTransactions } from '@/api/balance/balance';
import { DateTimeDisplay } from '@/components/ui/datetime-display';

export const BalanceHistoryPage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number | undefined>(new Date().getFullYear());

  const { data: balanceHistory } = useGetMyBalanceHistory({
    year: selectedYear,
  });

  const {
    data: transactionData,
    isLoading: isLoadingTransactions,
    isError: isTransactionsError,
  } = useGetMyTransactions({
    year: selectedYear,
    limit: 50,
  });

  const transactions = transactionData?.transactions || [];
  const balances = balanceHistory || [];

  // Get available years from balance history
  const availableYears = Array.from(new Set(balances.map(balance => balance.year))).sort((a, b) => b - a);

  // Add current year if not present
  const currentYear = new Date().getFullYear();
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
  }

  const currentBalance = balances.find(b => b.year === selectedYear);

  return (
    <div className='space-y-6'>
      {/* Page header */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Guthabenverlauf</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Übersicht über Ihr jährliches Guthaben und Transaktionsverlauf
          </CardDescription>
        </div>
      </section>

      {/* Year Filter */}
      {availableYears.length > 0 && (
        <section className='rounded-xl border border-neutral-hellgrau bg-white px-4 py-4'>
          <div className='flex flex-col gap-3'>
            <Label className='text-sm font-medium text-neutral-dunkelgrau'>Jahr auswählen</Label>
            <div className='flex gap-2 flex-wrap'>
              {availableYears.map(year => (
                <Button
                  key={year}
                  variant={selectedYear === year ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => setSelectedYear(year)}
                  className={selectedYear === year ? '' : 'hover:bg-swd-hellblau/40 hover:border-swd-tuerkis'}
                >
                  {year}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Current Balance Overview */}
      {currentBalance && (
        <section className='rounded-xl border border-neutral-hellgrau bg-white px-6 py-6'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <div className='text-center'>
              <div className='text-2xl font-bold text-swd-tuerkis-darker'>
                €{Number(currentBalance.total_credit).toFixed(2)}
              </div>
              <div className='text-sm text-muted-foreground'>Jahresguthaben</div>
            </div>
            <div className='text-center'>
              <div className='text-2xl font-bold text-red-600'>€{Number(currentBalance.used_credit).toFixed(2)}</div>
              <div className='text-sm text-muted-foreground'>Verwendet</div>
            </div>
            <div className='text-center'>
              <div className='text-2xl font-bold text-green-600'>
                €{Number(currentBalance.remaining_credit).toFixed(2)}
              </div>
              <div className='text-sm text-muted-foreground'>Verfügbar</div>
            </div>
          </div>

          {/* Usage Progress Bar */}
          <div className='mt-6'>
            <div className='flex justify-between text-sm text-muted-foreground mb-1'>
              <span>Verbrauch</span>
              <span>
                {((Number(currentBalance.used_credit) / Number(currentBalance.total_credit)) * 100).toFixed(1)}%
              </span>
            </div>
            <div className='w-full bg-neutral-hellgrau rounded-full h-2'>
              <div
                className='bg-swd-tuerkis h-2 rounded-full'
                style={{
                  width: `${Math.min(
                    (Number(currentBalance.used_credit) / Number(currentBalance.total_credit)) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Transaction History */}
      <section className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
        <div className='px-6 py-4 border-b border-neutral-hellgrau'>
          <CardTitle className='text-swd-tuerkis-darker text-xl font-semibold'>Transaktionsverlauf</CardTitle>
        </div>
        {isLoadingTransactions && (
          <div className='text-center py-12 bg-white'>
            <RefreshCw className='h-8 w-8 text-swd-tuerkis mx-auto mb-4 animate-spin' />
            <p className='text-muted-foreground mb-4'>Transaktionen werden geladen...</p>
          </div>
        )}

        {isTransactionsError && (
          <div className='text-center py-8 bg-white'>
            <Info className='h-8 w-8 text-red-500 mx-auto mb-4' />
            <p className='text-red-500 mb-4'>
              Fehler beim Laden der Transaktionen. Bitte versuchen Sie es später erneut.
            </p>
          </div>
        )}

        {!isLoadingTransactions && !isTransactionsError && transactions.length === 0 && (
          <div className='text-center py-8 bg-white'>
            <Info className='h-8 w-8 text-neutral-hellgrau mx-auto mb-4' />
            <p className='text-muted-foreground mb-4'>Keine Transaktionen für das gewählte Jahr gefunden.</p>
          </div>
        )}

        {!isLoadingTransactions && !isTransactionsError && transactions.length > 0 && (
          <Table className='min-w-[800px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap px-6 py-4'>
                  Datum
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap px-6 py-4'>
                  Art
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap px-6 py-4'>
                  Beschreibung
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right px-6 py-4'>
                  Betrag
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap px-6 py-4'>
                  Ablaufdatum
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map(transaction => (
                <TableRow key={transaction.id} className='border-b border-neutral-hellgrau hover:bg-swd-hellblau/40'>
                  <TableCell className='font-medium text-neutral-dunkelgrau align-middle px-6 py-4'>
                    <DateTimeDisplay date={transaction.created_at} format='short' />
                  </TableCell>
                  <TableCell className='align-middle px-6 py-4'>
                    <div className='flex items-center gap-2'>
                      {Number(transaction.amount) > 0 ? (
                        <TrendingUp className='h-4 w-4 text-green-500' />
                      ) : (
                        <TrendingDown className='h-4 w-4 text-red-500' />
                      )}
                      <Badge
                        variant={
                          transaction.transaction_type === 'ADJUSTMENT'
                            ? 'default'
                            : transaction.transaction_type === 'DEDUCTION'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {transaction.transaction_type === 'ADJUSTMENT'
                          ? 'Anpassung'
                          : transaction.transaction_type === 'DEDUCTION'
                            ? 'Abbuchung'
                            : transaction.transaction_type === 'CONTRACT_ALLOCATION'
                              ? 'Guthaben-Zuweisung'
                              : transaction.transaction_type}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className='align-middle px-6 py-4'>
                    <div className='truncate text-neutral-dunkelgrau'>
                      {transaction.description || 'Keine Beschreibung'}
                    </div>
                    {transaction.reference_id && (
                      <div className='text-xs text-muted-foreground'>
                        Ref: {transaction.reference_id.slice(0, 8)}...
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono align-middle font-medium px-6 py-4 ${
                      Number(transaction.amount) > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {Number(transaction.amount) > 0 ? '+' : ''}€{Number(transaction.amount).toFixed(2)}
                  </TableCell>
                  <TableCell className='align-middle px-6 py-4'>
                    {transaction.transaction_type === 'CONTRACT_ALLOCATION' &&
                    transaction.contract_reference_expires_at ? (
                      <DateTimeDisplay date={transaction.contract_reference_expires_at} format='short' />
                    ) : (
                      <span className='text-neutral-grau'>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* Balance History */}
      {balances.length > 1 && (
        <section className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
          <Table className='min-w-[700px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap px-6 py-4'>
                  Jahr
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right px-6 py-4'>
                  Jahresguthaben
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right px-6 py-4'>
                  Verwendet
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right px-6 py-4'>
                  Verbrauch %
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap text-right px-6 py-4'>
                  Verfügbar
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balances
                .sort((a, b) => b.year - a.year)
                .map(balance => {
                  const usagePercent =
                    Number(balance.total_credit) > 0
                      ? (Number(balance.used_credit) / Number(balance.total_credit)) * 100
                      : 0;

                  return (
                    <TableRow
                      key={`${balance.user_id}-${balance.year}`}
                      className='border-b border-neutral-hellgrau hover:bg-swd-hellblau/40'
                    >
                      <TableCell className='font-medium text-neutral-dunkelgrau align-middle px-6 py-4'>
                        {balance.year}
                      </TableCell>
                      <TableCell className='text-right align-middle font-medium text-neutral-grau px-6 py-4'>
                        €{Number(balance.total_credit).toFixed(2)}
                      </TableCell>
                      <TableCell className='text-right align-middle font-medium text-red-600 px-6 py-4'>
                        €{Number(balance.used_credit).toFixed(2)}
                      </TableCell>
                      <TableCell className='text-right align-middle px-6 py-4'>
                        <div className='flex items-center justify-end gap-2'>
                          <div className='w-16 bg-neutral-hellgrau rounded-full h-2'>
                            <div
                              className={`h-2 rounded-full ${
                                usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{
                                width: `${Math.min(usagePercent, 100)}%`,
                              }}
                            />
                          </div>
                          <span className='text-sm w-12 text-right text-neutral-grau'>{usagePercent.toFixed(0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell className='text-right align-middle font-medium text-green-600 px-6 py-4'>
                        €{Number(balance.remaining_credit).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </section>
      )}
    </div>
  );
};
